import { AxiosInstance } from 'axios';
import qs from 'qs';

import { OffsetPaginationInput } from '@app/data/inputs';
import { CollectionResource } from '@app/data/resources';

import {
    AssignTaskParticipantUsersInput,
    ChangeOpenTaskPositionWithinSameQueueInput,
    CreateTaskDraftInput,
    PickableTaskParticipantUserFilterInput,
    ReopenTaskInput,
    SetTaskComplexityParametersInput,
    SetTaskDeadlineInput,
    SetTaskPrimaryTeamInput,
    SwapTaskQueueInput,
    TaskFilterInput,
    TransitionOpenTaskQueueInput,
    TransitionTaskStateInput,
    UpdateTaskInformationInput,
    UpsertTaskClientFeedbackInput,
} from './inputs';
import { TaskRef, TaskRefList } from './refs';
import {
    PickableTaskParticipantUserResource,
    TaskIssueResource,
    TaskListItemResource,
    TaskPublishResource,
    TaskResource,
    TaskStateTransitionResource,
} from './resources';

export default class Task {
    private basePath = '/api/task';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Get list of users, which can be assigned to task
    getPickableParticipantUsers = async (
        pagination: OffsetPaginationInput,
        filter: PickableTaskParticipantUserFilterInput,
    ): Promise<CollectionResource<PickableTaskParticipantUserResource, {}>> => {
        const query = qs.stringify({ ...pagination, ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/picker/participant-user?${query}`);

        return data;
    };

    // Get task list
    get = async (
        pagination: OffsetPaginationInput,
        filter?: TaskFilterInput,
    ): Promise<CollectionResource<TaskListItemResource, {}>> => {
        const query = qs.stringify({ ...pagination, ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/?${query}`);

        return data;
    };

    // Get task info by ID
    getById = async (id: ID): Promise<TaskResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}`);

        return data;
    };

    // Get task publish information (if present)
    getPublishInformation = async (id: ID): Promise<TaskPublishResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}/publish`);

        return data;
    };

    // Swaps one task queue with other task
    swapQueue = async (input: SwapTaskQueueInput): Promise<TaskRefList> => {
        const { data } = await this.http.post(`${this.basePath}/queue-swap`, input);
        return data;
    };

    // Create new draft task
    create = async (input: CreateTaskDraftInput): Promise<TaskRef> => {
        const { data } = await this.http.post(`${this.basePath}/draft`, input);

        return data;
    };

    // Partially update task info
    update = async (id: ID, input: UpdateTaskInformationInput): Promise<TaskRef> => {
        const { data } = await this.http.patch(`${this.basePath}/${id}`, input);

        return data;
    };

    // Archive task
    archive = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}`);

        return data;
    };

    // Restore archived task
    restore = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/restore`);

        return data;
    };

    // Set task complexity parameters
    setTaskComplexity = async (id: ID, input: SetTaskComplexityParametersInput): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/complexity`, input);
        return data;
    };

    // Transition task state
    transitionState = async (id: ID, input: TransitionTaskStateInput): Promise<TaskRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/state`, input);

        return data;
    };

    // Reopen task from "DELIVERED" state
    reopen = async (id: ID, input: ReopenTaskInput): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/state/reopen`, input);

        return data;
    };

    // Pauses task
    pause = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/pause`);
        return data;
    };

    // Resumes paused task
    resume = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/pause`);
        return data;
    };

    // Get task state transition log
    getTaskStateTransitionLog = async (id: ID): Promise<CollectionResource<TaskStateTransitionResource, {}>> => {
        const { data } = await this.http.get(`${this.basePath}/${id}/state`);

        return data;
    };

    // Set deadline
    setDeadline = async (id: ID, input: SetTaskDeadlineInput): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/deadline`, input);

        return data;
    };

    // Remove deadline
    removeDeadline = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/deadline`);

        return data;
    };

    // Join task as a participant
    joinAsParticipant = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/participation/join`);

        return data;
    };

    // Assign participant users
    assignParticipantUsers = async (id: ID, input: AssignTaskParticipantUsersInput): Promise<TaskRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/participation`, input);

        return data;
    };

    // Remove user participation from a task
    removeParticipation = async (id: ID, userId: ID): Promise<TaskRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/participation/${userId}`);

        return data;
    };

    // Create / Update client feedback (when state = 'delivered')
    setClientFeedback = async (id: ID, input: UpsertTaskClientFeedbackInput): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/feedback/client`, input);

        return data;
    };

    // Change open task within same queue
    changeOpenTaskPositionWithinSameQueue = async (
        id: ID,
        input: ChangeOpenTaskPositionWithinSameQueueInput,
    ): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/queue/position`, input);
        return data;
    };

    // Lock task queue automation
    lockTaskQueueAutomation = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/queue/lock`);
        return data;
    };

    // Unlock task queue automation
    unlockTaskQueueAutomation = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/queue/lock`);
        return data;
    };

    // Change queue of the open task
    transitionOpenTaskQueue = async (id: ID, input: TransitionOpenTaskQueueInput): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/queue`, input);
        return data;
    };

    // Elevates task priority, marking it as "high" priority
    elevatePriority = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/priority`);
        return data;
    };

    // Removes elevated priority from task
    removePriorityElevation = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/priority`);
        return data;
    };

    // Returns list of task issues
    getIssueList = async (id: ID): Promise<CollectionResource<TaskIssueResource, {}>> => {
        const { data } = await this.http.get(`${this.basePath}/${id}/issue`);

        return data;
    };

    // Sets task primary team
    setPrimaryTeam = async (id: ID, input: SetTaskPrimaryTeamInput): Promise<TaskRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/team/primary`, input);

        return data;
    };

    // Remove currently assigned primary team
    removePrimaryTeam = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/team/primary`);

        return data;
    };

    // Attempt to generate shadow ID
    generateShadowId = async (id: ID): Promise<TaskRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/shadow-id/generate`);

        return data;
    };
}
